/* When a binding runs, said in a table cell.
 *
 * **Short, and built here rather than taken from the server's `describes`.** `describes` is the full
 * sentence — "Every day at 11:36 Asia/Kolkata, full scan" — and two of them joined for a binding that
 * is both scheduled and harvest-driven ran to a hundred characters and pushed the bindings table off
 * the screen. The cell says what the design says ("Daily at 11:36", "After harvest · warehouse") and
 * carries the server's sentences on hover, so nothing is lost and the column stays a column.
 */

import type { TFunction } from 'i18next';

import type { SchedulingSummary } from '@/shared/api/generated/rules';

import { coversEveryDay, weekdayKey } from './presentation';

export interface RunsLabel {
  /** What the cell shows. */
  readonly short: string;
  /** The server's own sentences, for the cell's title. */
  readonly full: string;
}

function scheduleText(t: TFunction, summary: SchedulingSummary): string {
  const schedule = summary.schedule;
  const time = schedule.local_time ?? '';
  const days = schedule.weekdays ?? [];
  const recurrence =
    schedule.cadence === 'weekly' && !coversEveryDay(days)
      ? t('scheduling.short.weekly', {
          days: days.map((day) => t(weekdayKey(day))).join(', '),
          time,
        })
      : t('scheduling.short.daily', { time });
  return schedule.strategy === 'sampled'
    ? t('scheduling.short.sampled', { label: recurrence })
    : recurrence;
}

export function runsLabel(
  t: TFunction,
  summary: SchedulingSummary,
  connectionName: (id: string) => string | null,
): RunsLabel {
  const scheduled = summary.schedule.configured && summary.schedule.enabled;
  const afterHarvest = summary.trigger.configured && summary.trigger.enabled;
  const full = [
    scheduled ? (summary.schedule.describes ?? null) : null,
    afterHarvest ? (summary.trigger.describes ?? null) : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

  if (scheduled && afterHarvest) {
    return {
      short: t('scheduling.short.both', { schedule: scheduleText(t, summary) }),
      full,
    };
  }
  if (scheduled) return { short: scheduleText(t, summary), full };
  if (afterHarvest) {
    const id = summary.trigger.connection_id;
    const name = id === undefined ? null : connectionName(id);
    return {
      short:
        name === null
          ? t('scheduling.short.harvest')
          : t('scheduling.short.harvestOf', { name }),
      full,
    };
  }
  return { short: t('rules.runs.manual'), full: t('rules.runs.manual') };
}
